import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, ArrowRight, Building2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
interface BusinessPhoto {
  photo_url: string;
  display_order: number;
}

interface Business {
  id: string;
  business_name: string;
  category: string;
  address: string;
  state?: string | null;
  postcode?: string | null;
  description: string | null;
  business_logo: string | null;
  business_photos?: BusinessPhoto[];
}

export function FeaturedProviders() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    // Fetch businesses from public view - view already filters for active/trial and not suspended
    const { data: businessData, error } = await supabase
      .from("businesses_public")
      .select("id, business_name, category, address, state, postcode, description, business_logo")
      .limit(4);

    if (!error && businessData) {
      // Fetch photos for each business
      const businessesWithPhotos = await Promise.all(
        businessData.map(async (business) => {
          const { data: photos } = await supabase
            .from("business_photos")
            .select("photo_url, display_order")
            .eq("business_id", business.id)
            .order("display_order", { ascending: true })
            .limit(1);

          return {
            ...business,
            business_photos: photos || [],
          };
        })
      );

      setBusinesses(businessesWithPhotos);
    }
    setLoading(false);
  };

  const formatCategory = (category: string) => {
    return category
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getBusinessImage = (business: Business) => {
    // First try to get the first uploaded photo
    if (business.business_photos && business.business_photos.length > 0) {
      return business.business_photos[0].photo_url;
    }
    // Fall back to business logo
    return business.business_logo;
  };

  // Don't render the section if there are no businesses
  if (!loading && businesses.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">
              Featured Wellness Providers
            </h2>
            <p className="text-muted-foreground">
              Top-rated wellness businesses loved by thousands
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/browse">
              View All
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <Card key={i} variant="elevated" className="h-64 animate-pulse bg-muted" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {businesses.map((business, index) => {
              const imageUrl = getBusinessImage(business);
              
              return (
                <Link
                  key={business.id}
                  to={`/provider/${business.id}`}
                  className="group"
                >
                  <Card 
                    variant="elevated" 
                    className="overflow-hidden h-full animate-fade-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Image/Logo */}
                    <div className="relative h-48 overflow-hidden bg-gradient-to-br from-primary/20 to-wellness-teal/20">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={business.business_name}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building2 className="w-16 h-16 text-primary/40" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent" />
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {formatCategory(business.category)}
                        </Badge>
                      </div>
                      
                      <h3 className="font-display text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-1">
                        {business.business_name}
                      </h3>

                      {/* Location */}
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span className="line-clamp-1">{[business.address, business.state, business.postcode].filter(Boolean).join(', ')}</span>
                      </div>

                      {/* Description */}
                      {business.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {business.description}
                        </p>
                      )}
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}